import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import { getFinanceSummary, getDeliveryFee, updateDeliveryFee, getDailySales, getMonthlyConcentrado, getBranches } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Building2,
  Users,
  Loader2,
  ChevronDown,
  ChevronUp,
  Wallet,
  Zap,
  Droplets,
  Wifi,
  Home,
  MoreHorizontal,
  CreditCard,
  Banknote,
  ArrowRightLeft,
  CalendarDays,
  PieChart
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_CONFIG = {
  renta: { label: 'Renta', icon: Home, color: 'bg-rose-100 text-rose-700' },
  luz: { label: 'Luz', icon: Zap, color: 'bg-amber-100 text-amber-700' },
  agua: { label: 'Agua', icon: Droplets, color: 'bg-cyan-100 text-cyan-700' },
  internet: { label: 'Internet', icon: Wifi, color: 'bg-violet-100 text-violet-700' },
  otro: { label: 'Otro', icon: MoreHorizontal, color: 'bg-slate-100 text-slate-700' }
};

export default function AdminFinancePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [expandedBranch, setExpandedBranch] = useState(null);
  const [expandedPayroll, setExpandedPayroll] = useState(false);
  const [deliveryFee, setDeliveryFee] = useState(20);
  const [deliveryFeeDraft, setDeliveryFeeDraft] = useState('20');
  const [savingFee, setSavingFee] = useState(false);
  const [dailySales, setDailySales] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [activeBranchId, setActiveBranchId] = useState(null); // null = General (all)

  useEffect(() => {
    fetchData();
    fetchDailySales();
  }, [month, activeBranchId]);

  useEffect(() => {
    getBranches()
      .then((r) => setBranches(r.data || []))
      .catch(() => { /* silent */ });
    getDeliveryFee()
      .then((res) => {
        const v = Number(res.data?.amount ?? 20);
        setDeliveryFee(v);
        setDeliveryFeeDraft(String(v));
      })
      .catch(() => { /* silent */ });
  }, []);

  const handleSaveDeliveryFee = async () => {
    const n = parseFloat(deliveryFeeDraft);
    if (Number.isNaN(n) || n < 0) {
      toast.error('Ingresa un monto válido');
      return;
    }
    setSavingFee(true);
    try {
      const res = await updateDeliveryFee(n);
      setDeliveryFee(Number(res.data.amount));
      toast.success('Cargo a domicilio actualizado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar');
    }
    setSavingFee(false);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getFinanceSummary(month, activeBranchId);
      setData(res.data);
    } catch (error) {
      toast.error('Error al cargar datos financieros');
    }
    setLoading(false);
  };

  const fetchDailySales = async () => {
    setDailyLoading(true);
    try {
      const res = await getDailySales(month);
      setDailySales(res.data);
    } catch (error) {
      toast.error('Error al cargar ventas diarias');
    }
    setDailyLoading(false);
  };

  const activeBranchName = activeBranchId
    ? (branches.find((b) => b.id === activeBranchId)?.name || 'Sucursal')
    : 'General';

  const monthLabel = () => {
    if (!month) return '';
    const [y, m] = month.split('-');
    const date = new Date(parseInt(y), parseInt(m) - 1);
    return date.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  const balance = data?.balance || {};
  const isPositive = balance.resultado >= 0;

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fadeIn">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" data-testid="finance-title">Finanzas</h1>
            <p className="text-slate-600 mt-1 capitalize">
              Resumen financiero · {monthLabel()}
              {activeBranchId && <span className="ml-1 text-blue-600 font-medium">· {activeBranchName}</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/admin/finanzas/balance"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors"
              data-testid="open-balance-mensual-btn"
            >
              <PieChart className="w-4 h-4" />
              Balance Mensual
            </Link>
            <Input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="w-auto"
              data-testid="finance-month-input"
            />
          </div>
        </div>

        {/* Branch tabs (General + one per branch) */}
        <div className="flex flex-wrap gap-1 border-b border-slate-200" data-testid="finance-branch-tabs">
          <BranchTab
            active={activeBranchId === null}
            onClick={() => setActiveBranchId(null)}
            icon={PieChart}
            label="General"
            testId="branch-tab-general"
          />
          {branches.map((b) => (
            <BranchTab
              key={b.id}
              active={activeBranchId === b.id}
              onClick={() => setActiveBranchId(b.id)}
              icon={Building2}
              label={b.name}
              testId={`branch-tab-${b.id}`}
            />
          ))}
        </div>

        {/* Delivery fee configuration */}
        <div className="card-base p-5 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4" data-testid="delivery-fee-card">
          <div>
            <div className="text-xs font-semibold text-amber-600 uppercase tracking-wide">Cargo a domicilio / recogida</div>
            <div className="text-sm text-slate-500 mt-1 max-w-xl">
              Se agrega automáticamente como línea "servicio extra" al agendar domicilio o recogida. Al cobrar en el POS se puede ajustar.
            </div>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <label className="text-xs text-slate-500 mb-1 block" htmlFor="delivery-fee-input">Monto actual</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                <Input
                  id="delivery-fee-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={deliveryFeeDraft}
                  onChange={(e) => setDeliveryFeeDraft(e.target.value)}
                  className="pl-7 w-32 font-mono font-semibold"
                  data-testid="delivery-fee-input"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={handleSaveDeliveryFee}
              disabled={savingFee || parseFloat(deliveryFeeDraft) === deliveryFee}
              className="h-10 px-4 rounded-lg bg-amber-500 text-white font-semibold hover:bg-amber-600 disabled:opacity-50"
              data-testid="save-delivery-fee-btn"
            >
              {savingFee ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>

        {/* Balance Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="finance-summary-cards">
          <SummaryCard
            title="Ingresos"
            value={formatCurrency(data?.income?.total || 0)}
            subtitle={`${data?.income?.orders_count || 0} ordenes`}
            icon={TrendingUp}
            colorClass="bg-emerald-50 border-emerald-200"
            iconClass="bg-emerald-100 text-emerald-600"
            valueClass="text-emerald-700"
            testId="finance-income"
          />
          <SummaryCard
            title="Gastos Fijos"
            value={formatCurrency(data?.expenses?.total || 0)}
            subtitle="Sucursales"
            icon={Building2}
            colorClass="bg-rose-50 border-rose-200"
            iconClass="bg-rose-100 text-rose-600"
            valueClass="text-rose-700"
            testId="finance-expenses"
          />
          <SummaryCard
            title="Nomina"
            value={formatCurrency(data?.payroll?.total || 0)}
            subtitle={`${data?.payroll?.employees?.length || 0} empleados`}
            icon={Users}
            colorClass="bg-amber-50 border-amber-200"
            iconClass="bg-amber-100 text-amber-600"
            valueClass="text-amber-700"
            testId="finance-payroll"
          />
          <SummaryCard
            title="Balance"
            value={formatCurrency(Math.abs(balance.resultado || 0))}
            subtitle={isPositive ? 'Ganancia' : 'Perdida'}
            icon={Wallet}
            colorClass={isPositive ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-300'}
            iconClass={isPositive ? 'bg-blue-100 text-blue-600' : 'bg-red-100 text-red-600'}
            valueClass={isPositive ? 'text-blue-700' : 'text-red-700'}
            prefix={isPositive ? '+' : '-'}
            testId="finance-balance"
          />
        </div>

        {/* Two columns */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Income Breakdown */}
          <div className="card-base p-6" data-testid="finance-income-detail">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              Ingresos por Metodo de Pago
            </h2>
            <div className="space-y-3">
              <PaymentMethodRow
                icon={Banknote}
                label="Efectivo"
                amount={data?.income?.by_method?.efectivo || 0}
                total={data?.income?.total || 1}
                color="emerald"
              />
              <PaymentMethodRow
                icon={CreditCard}
                label="Tarjeta"
                amount={data?.income?.by_method?.tarjeta || 0}
                total={data?.income?.total || 1}
                color="blue"
              />
              <PaymentMethodRow
                icon={ArrowRightLeft}
                label="Transferencia"
                amount={data?.income?.by_method?.transferencia || 0}
                total={data?.income?.total || 1}
                color="violet"
              />
            </div>
          </div>

          {/* Expenses by Category */}
          <div className="card-base p-6" data-testid="finance-expenses-category">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-rose-600" />
              Gastos Fijos por Categoria
            </h2>
            {Object.keys(data?.expenses?.by_category || {}).length === 0 ? (
              <p className="text-slate-400 text-center py-6">No hay gastos registrados</p>
            ) : (
              <div className="space-y-3">
                {Object.entries(data?.expenses?.by_category || {}).sort((a, b) => b[1] - a[1]).map(([cat, amount]) => {
                  const config = CATEGORY_CONFIG[cat] || CATEGORY_CONFIG.otro;
                  const Icon = config.icon;
                  const pct = data?.expenses?.total ? Math.round((amount / data.expenses.total) * 100) : 0;
                  return (
                    <div key={cat} className="flex items-center gap-3" data-testid={`expense-cat-${cat}`}>
                      <div className={`p-2 rounded-lg ${config.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-slate-700 capitalize">{config.label}</span>
                          <span className="font-semibold text-slate-900">{formatCurrency(amount)}</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-2">
                          <div className="h-2 rounded-full bg-rose-400 transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                      <span className="text-xs font-medium text-slate-500 w-10 text-right">{pct}%</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Expenses by Branch */}
        <div className="card-base p-6" data-testid="finance-expenses-branch">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-indigo-600" />
            Gastos por Sucursal
          </h2>
          {Object.keys(data?.expenses?.by_branch || {}).length === 0 ? (
            <p className="text-slate-400 text-center py-6">No hay gastos por sucursal</p>
          ) : (
            <div className="space-y-3">
              {Object.entries(data?.expenses?.by_branch || {}).map(([branchName, info]) => (
                <div key={branchName} className="border border-slate-200 rounded-xl overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                    onClick={() => setExpandedBranch(expandedBranch === branchName ? null : branchName)}
                    data-testid={`branch-expense-${branchName}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-indigo-100 rounded-lg">
                        <Building2 className="w-4 h-4 text-indigo-600" />
                      </div>
                      <span className="font-semibold text-slate-800">{branchName}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-rose-600">{formatCurrency(info.total)}</span>
                      {expandedBranch === branchName ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    </div>
                  </button>
                  {expandedBranch === branchName && (
                    <div className="border-t border-slate-100 p-4 bg-slate-50 space-y-2">
                      {Object.entries(info.categories || {}).map(([cat, amt]) => {
                        const config = CATEGORY_CONFIG[cat] || CATEGORY_CONFIG.otro;
                        const Icon = config.icon;
                        return (
                          <div key={cat} className="flex items-center justify-between py-1.5">
                            <div className="flex items-center gap-2">
                              <Icon className="w-4 h-4 text-slate-500" />
                              <span className="text-sm text-slate-700 capitalize">{config.label}</span>
                            </div>
                            <span className="text-sm font-semibold text-slate-800">{formatCurrency(amt)}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Payroll */}
        <div className="card-base p-6" data-testid="finance-payroll-detail">
          <button
            className="w-full flex items-center justify-between"
            onClick={() => setExpandedPayroll(!expandedPayroll)}
          >
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-amber-600" />
              Nomina - Empleados
              {data?.payroll?.is_partial && (
                <span className="text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full" data-testid="payroll-prorate-badge">
                  Prorrateado · día {data.payroll.day_of_month}/{data.payroll.days_in_month}
                </span>
              )}
            </h2>
            <div className="flex items-center gap-3">
              <span className="text-lg font-bold text-amber-700">{formatCurrency(data?.payroll?.total || 0)}</span>
              {expandedPayroll ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
            </div>
          </button>

          {expandedPayroll && (
            <div className="mt-4 overflow-x-auto">
              {(data?.payroll?.employees || []).length === 0 ? (
                <p className="text-slate-400 text-center py-6">No hay empleados con sueldo asignado</p>
              ) : (
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="table-header">Empleado</th>
                      <th className="table-header">Rol</th>
                      <th className="table-header text-right">Sueldo</th>
                      <th className="table-header text-right">Bono</th>
                      <th className="table-header text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.payroll?.employees || []).map((emp) => (
                      <tr key={emp.id} className="table-row" data-testid={`payroll-row-${emp.id}`}>
                        <td className="table-cell font-medium text-slate-900">{emp.name}</td>
                        <td className="table-cell capitalize text-slate-600">{emp.role}</td>
                        <td className="table-cell text-right">{formatCurrency(emp.salary)}</td>
                        <td className="table-cell text-right text-emerald-600">{emp.bonus > 0 ? `+${formatCurrency(emp.bonus)}` : '—'}</td>
                        <td className="table-cell text-right font-semibold text-slate-900">{formatCurrency(emp.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        {/* Ventas diarias por sucursal */}
        <DailySalesTable data={dailySales} loading={dailyLoading} />

        {/* Concentrado mensual por sucursal */}
        <MonthlyConcentrado month={month} />

        {/* Balance Summary */}
        <div className={`card-base p-6 border-2 ${isPositive ? 'border-emerald-200 bg-emerald-50/30' : 'border-red-200 bg-red-50/30'}`} data-testid="finance-balance-detail">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <DollarSign className={`w-5 h-5 ${isPositive ? 'text-emerald-600' : 'text-red-600'}`} />
            Resumen del Balance
          </h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2">
              <span className="text-slate-600">Ingresos (Ventas POS)</span>
              <span className="font-semibold text-emerald-700">+{formatCurrency(balance.total_ingresos || 0)}</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-slate-600">Gastos Fijos</span>
              <span className="font-semibold text-rose-600">-{formatCurrency(data?.expenses?.total || 0)}</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-slate-600">Nomina</span>
              <span className="font-semibold text-amber-600">-{formatCurrency(data?.payroll?.total || 0)}</span>
            </div>
            <div className="border-t-2 border-slate-300 pt-3">
              <div className="flex items-center justify-between">
                <span className="text-lg font-bold text-slate-900">Resultado</span>
                <span className={`text-2xl font-bold ${isPositive ? 'text-emerald-700' : 'text-red-700'}`}>
                  {isPositive ? '+' : '-'}{formatCurrency(Math.abs(balance.resultado || 0))}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function SummaryCard({ title, value, subtitle, icon: Icon, colorClass, iconClass, valueClass, prefix, testId }) {
  return (
    <div className={`rounded-xl border p-5 ${colorClass}`} data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-600">{title}</p>
          <p className={`text-2xl font-bold mt-1 ${valueClass}`}>{prefix || ''}{value}</p>
          <p className="text-xs text-slate-500 mt-1">{subtitle}</p>
        </div>
        <div className={`p-2.5 rounded-xl ${iconClass}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

function PaymentMethodRow({ icon: Icon, label, amount, total, color }) {
  const pct = total > 0 ? Math.round((amount / total) * 100) : 0;
  const colors = {
    emerald: 'bg-emerald-400',
    blue: 'bg-blue-400',
    violet: 'bg-violet-400'
  };
  return (
    <div className="flex items-center gap-3">
      <Icon className="w-4 h-4 text-slate-500" />
      <div className="flex-1">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-slate-700">{label}</span>
          <span className="text-sm font-semibold text-slate-900">{formatCurrency(amount)}</span>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-1.5">
          <div className={`h-1.5 rounded-full ${colors[color]} transition-all`} style={{ width: `${pct}%` }} />
        </div>
      </div>
      <span className="text-xs text-slate-500 w-10 text-right">{pct}%</span>
    </div>
  );
}

function DailySalesTable({ data, loading }) {
  const formatDay = (iso) => {
    try {
      const [y, m, d] = iso.split('-').map(Number);
      const dt = new Date(y, m - 1, d);
      return dt.toLocaleDateString('es-MX', { weekday: 'short', day: '2-digit', month: 'short' });
    } catch {
      return iso;
    }
  };

  const branches = data?.branches || [];
  const days = data?.days || [];
  const totalsByBranch = data?.totals?.by_branch || {};
  const grandTotal = data?.totals?.grand_total || 0;

  return (
    <div className="card-base p-6" data-testid="finance-daily-sales">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-blue-600" />
          Ventas diarias por sucursal
        </h2>
        <span className="text-sm font-semibold text-blue-700 bg-blue-50 px-3 py-1 rounded-full">
          Total mes: {formatCurrency(grandTotal)}
        </span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      ) : days.length === 0 || branches.length === 0 ? (
        <p className="text-slate-400 text-center py-8" data-testid="daily-sales-empty">
          No hay ventas registradas este mes
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="table-header sticky left-0 bg-white">Fecha</th>
                {branches.map((b) => (
                  <th key={b.id} className="table-header text-right" data-testid={`daily-sales-branch-header-${b.id}`}>
                    {b.name}
                  </th>
                ))}
                <th className="table-header text-right bg-slate-50">Total día</th>
              </tr>
            </thead>
            <tbody>
              {days.map((d) => (
                <tr key={d.date} className="table-row" data-testid={`daily-sales-row-${d.date}`}>
                  <td className="table-cell font-medium text-slate-800 capitalize sticky left-0 bg-white">
                    {formatDay(d.date)}
                  </td>
                  {branches.map((b) => {
                    const cell = d.by_branch?.[b.id];
                    return (
                      <td key={b.id} className="table-cell text-right tabular-nums">
                        {cell ? (
                          <div>
                            <div className="font-semibold text-slate-900">{formatCurrency(cell.total)}</div>
                            <div className="text-xs text-slate-500">{cell.orders} órd.</div>
                          </div>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="table-cell text-right font-bold text-blue-700 tabular-nums bg-slate-50">
                    {formatCurrency(d.total)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-300 bg-slate-50">
                <td className="table-cell font-bold text-slate-900 sticky left-0 bg-slate-50">Total mes</td>
                {branches.map((b) => (
                  <td key={b.id} className="table-cell text-right font-bold text-slate-900 tabular-nums" data-testid={`daily-sales-branch-total-${b.id}`}>
                    {formatCurrency(totalsByBranch[b.id] || 0)}
                  </td>
                ))}
                <td className="table-cell text-right font-bold text-blue-700 tabular-nums" data-testid="daily-sales-grand-total">
                  {formatCurrency(grandTotal)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}



function MonthlyConcentrado({ month }) {
  const [branches, setBranches] = useState([]);
  const [selected, setSelected] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getBranches().then((r) => setBranches(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const branchId = selected === 'all' ? null : selected;
    getMonthlyConcentrado(month, branchId)
      .then((r) => setData(r.data))
      .catch(() => toast.error('Error al cargar concentrado mensual'))
      .finally(() => setLoading(false));
  }, [month, selected]);

  const monthLabel = () => {
    if (!data?.month) return '';
    const [y, m] = data.month.split('-');
    const date = new Date(parseInt(y), parseInt(m) - 1);
    return date.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="card-base p-6" data-testid="monthly-concentrado">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <PieChart className="w-5 h-5 text-indigo-600" />
            Concentrado Mensual
          </h2>
          <p className="text-xs text-slate-500 mt-1 capitalize">{monthLabel()} · {data?.branch_name || 'Cargando…'}</p>
        </div>
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-slate-500" />
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger className="w-52" data-testid="concentrado-branch-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="concentrado-branch-all">Todas las sucursales</SelectItem>
              {branches.map((b) => (
                <SelectItem key={b.id} value={b.id} data-testid={`concentrado-branch-${b.id}`}>
                  {b.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading || !data ? (
        <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>
      ) : (
        <div className="space-y-5">
          {/* Tarjetas KPI */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <KpiCard label="Ingresos" value={formatCurrency(data.income.total)} sub={`${data.income.orders_count} ventas`} color="emerald" icon={TrendingUp} testId="concentrado-income" />
            <KpiCard label="Gastos fijos" value={formatCurrency(data.expenses.total)} sub={`${data.expenses.count} cargos`} color="rose" icon={TrendingDown} testId="concentrado-expenses" />
            <KpiCard label="Balance" value={formatCurrency(data.balance)} sub={data.balance >= 0 ? 'Resultado positivo' : 'Resultado negativo'} color={data.balance >= 0 ? 'blue' : 'rose'} icon={Wallet} testId="concentrado-balance" />
          </div>

          {/* Métodos de pago */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 uppercase mb-3">Ingresos por método</h3>
            <div className="space-y-2">
              <PaymentMethodRow icon={Banknote} label="Efectivo" amount={data.income.by_method.efectivo || 0} total={data.income.total} color="emerald" />
              <PaymentMethodRow icon={CreditCard} label="Tarjeta" amount={data.income.by_method.tarjeta || 0} total={data.income.total} color="blue" />
              <PaymentMethodRow icon={ArrowRightLeft} label="Transferencia" amount={data.income.by_method.transferencia || 0} total={data.income.total} color="violet" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Categorías de gasto */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 uppercase mb-3">Gastos por categoría</h3>
              {Object.keys(data.expenses.by_category).length === 0 ? (
                <p className="text-slate-400 text-sm">Sin gastos registrados este mes</p>
              ) : (
                <ul className="space-y-2">
                  {Object.entries(data.expenses.by_category).map(([cat, amt]) => {
                    const cfg = CATEGORY_CONFIG[cat] || CATEGORY_CONFIG.otro;
                    const Icon = cfg.icon;
                    return (
                      <li key={cat} className="flex items-center justify-between p-2 rounded-lg bg-slate-50">
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 rounded-md ${cfg.color}`}><Icon className="w-3.5 h-3.5" /></div>
                          <span className="text-sm text-slate-700">{cfg.label}</span>
                        </div>
                        <span className="font-semibold text-slate-900 tabular-nums">{formatCurrency(amt)}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Top servicios */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 uppercase mb-3">Top servicios</h3>
              {(!data.top_services || data.top_services.length === 0) ? (
                <p className="text-slate-400 text-sm">Sin servicios registrados este mes</p>
              ) : (
                <ul className="space-y-2">
                  {data.top_services.map((s, idx) => (
                    <li key={s.name} className="flex items-center justify-between p-2 rounded-lg bg-slate-50" data-testid={`concentrado-service-${idx}`}>
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center">{idx + 1}</span>
                        <span className="text-sm text-slate-700">{s.name}</span>
                      </div>
                      <span className="font-semibold text-slate-900 text-sm">{s.count}x</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, sub, color, icon: Icon, testId }) {
  const palette = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
  };
  return (
    <div className={`rounded-xl border p-4 ${palette[color]}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase">
        <Icon className="w-4 h-4" />
        {label}
      </div>
      <div className="text-2xl font-bold tabular-nums mt-1">{value}</div>
      <div className="text-xs opacity-75 mt-0.5">{sub}</div>
    </div>
  );
}

function BranchTab({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
        active
          ? 'border-blue-600 text-blue-700 bg-blue-50/60'
          : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50'
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}

